const MAX_FILE_BYTES = 6 * 1024 * 1024 * 1024
const OPENING_SAMPLE_COUNT = 10
const MIDDLE_SAMPLE_COUNT = 12
const MIDDLE_EXTRA_SAMPLE_COUNT = 4
const CLOSING_SAMPLE_COUNT = 10
const FRAMES_PER_SHEET = 4
const DECODER_COUNT = 4
const CELL_WIDTH = 960
const CELL_HEIGHT = 540
const TIMESTAMP_QUANTUM_SECONDS = 30

function formatDuration(totalSeconds) {
  const rounded = Math.max(0, Math.round(Number(totalSeconds) || 0))
  const hours = Math.floor(rounded / 3600)
  const minutes = Math.floor((rounded % 3600) / 60)
  const seconds = rounded % 60
  return [hours, minutes, seconds]
    .map(value => String(value).padStart(2, '0'))
    .join(':')
}

function rangeTimestamps(duration, startFraction, endFraction, count) {
  const start = duration * startFraction
  const width = duration * (endFraction - startFraction)
  return Array.from({ length: count }, (_, index) => (
    start + width * ((index + 0.5) / count)
  ))
}

function gapFillTimestamps(
  duration,
  startFraction,
  endFraction,
  existingCount,
  extraCount,
) {
  const start = duration * startFraction
  const width = duration * (endFraction - startFraction)
  return Array.from({ length: extraCount }, (_, index) => {
    const gapIndex = Math.round(((index + 0.5) * existingCount) / extraCount)
    return Math.min(
      duration - 0.05,
      Math.max(0.05, start + width * (gapIndex / existingCount)),
    )
  })
}

export function ceremonySkimTimestamps(duration) {
  return [
    ...rangeTimestamps(duration, 0.005, 0.2, OPENING_SAMPLE_COUNT),
    ...rangeTimestamps(duration, 0.2, 0.8, MIDDLE_SAMPLE_COUNT),
    ...gapFillTimestamps(
      duration,
      0.2,
      0.8,
      MIDDLE_SAMPLE_COUNT,
      MIDDLE_EXTRA_SAMPLE_COUNT,
    ),
    ...rangeTimestamps(duration, 0.8, 0.995, CLOSING_SAMPLE_COUNT),
  ]
    .map(seconds => Math.min(
      duration - 0.05,
      Math.max(
        0.05,
        Math.round(seconds / TIMESTAMP_QUANTUM_SECONDS) * TIMESTAMP_QUANTUM_SECONDS,
      ),
    ))
    .sort((left, right) => left - right)
}

function waitForVideo(video, eventName, errorMessage) {
  if (eventName === 'loadeddata' && video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
    return Promise.resolve()
  }
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      cleanup()
      reject(new Error(errorMessage))
    }, 5000)
    const onReady = () => {
      cleanup()
      resolve()
    }
    const onError = () => {
      cleanup()
      reject(new Error(errorMessage))
    }
    const cleanup = () => {
      window.clearTimeout(timeout)
      video.removeEventListener(eventName, onReady)
      video.removeEventListener('error', onError)
    }
    video.addEventListener(eventName, onReady, { once: true })
    video.addEventListener('error', onError, { once: true })
  })
}

async function createDecoder(fileUrl) {
  const video = document.createElement('video')
  video.preload = 'auto'
  video.muted = true
  video.playsInline = true
  video.src = fileUrl
  const metadata = waitForVideo(
    video,
    'loadedmetadata',
    'The browser could not read this MP4.',
  )
  video.load()
  await metadata
  await waitForVideo(
    video,
    'loadeddata',
    'A local Ceremony Skim decoder did not become ready.',
  )
  return video
}

function releaseDecoder(video) {
  video.removeAttribute('src')
  video.load()
}

function seek(video, seconds) {
  if (Math.abs(video.currentTime - seconds) < 0.001) return Promise.resolve()
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      cleanup()
      reject(new Error(`Local sampling timed out at ${formatDuration(seconds)}.`))
    }, 5000)
    const onSeeked = () => {
      cleanup()
      resolve()
    }
    const onError = () => {
      cleanup()
      reject(new Error(`Could not decode ${formatDuration(seconds)}.`))
    }
    const cleanup = () => {
      window.clearTimeout(timeout)
      video.removeEventListener('seeked', onSeeked)
      video.removeEventListener('error', onError)
    }
    video.addEventListener('seeked', onSeeked, { once: true })
    video.addEventListener('error', onError, { once: true })
    video.currentTime = seconds
  })
}

async function captureFrame(video, seconds, index) {
  await seek(video, seconds)
  const canvas = document.createElement('canvas')
  canvas.width = CELL_WIDTH
  canvas.height = CELL_HEIGHT
  const context = canvas.getContext('2d', { alpha: false })
  context.fillStyle = '#101619'
  context.fillRect(0, 0, CELL_WIDTH, CELL_HEIGHT)

  const scale = Math.min(
    CELL_WIDTH / video.videoWidth,
    CELL_HEIGHT / video.videoHeight,
  )
  const width = video.videoWidth * scale
  const height = video.videoHeight * scale
  context.drawImage(
    video,
    (CELL_WIDTH - width) / 2,
    (CELL_HEIGHT - height) / 2,
    width,
    height,
  )

  const gradient = context.createLinearGradient(0, CELL_HEIGHT - 96, 0, CELL_HEIGHT)
  gradient.addColorStop(0, 'rgba(0, 0, 0, 0)')
  gradient.addColorStop(1, 'rgba(0, 0, 0, 0.84)')
  context.fillStyle = gradient
  context.fillRect(0, CELL_HEIGHT - 108, CELL_WIDTH, 108)
  context.fillStyle = '#ffffff'
  context.font = '700 30px ui-monospace, SFMono-Regular, Menlo, monospace'
  context.fillText(formatDuration(seconds), 22, CELL_HEIGHT - 24)
  context.textAlign = 'right'
  context.font = '700 22px Inter, system-ui, sans-serif'
  context.fillStyle = '#ffd1c5'
  context.fillText(
    `frame ${String(index + 1).padStart(2, '0')}`,
    CELL_WIDTH - 20,
    CELL_HEIGHT - 26,
  )
  return { seconds, canvas }
}

async function captureFrames(videos, timestamps, onProgress) {
  const frames = new Array(timestamps.length)
  let nextIndex = 0
  let completed = 0

  const runDecoder = async video => {
    while (nextIndex < timestamps.length) {
      const index = nextIndex
      nextIndex += 1
      frames[index] = await captureFrame(video, timestamps[index], index)
      completed += 1
      if (onProgress) {
        onProgress({
          phase: 'sampling',
          progress: Math.round((completed / timestamps.length) * 70),
          message: `Sampled ${completed} of ${timestamps.length} frames locally`,
        })
      }
    }
  }

  await Promise.all(videos.map(runDecoder))
  return frames
}

function canvasToBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) resolve(blob)
      else reject(new Error('Could not encode a Ceremony Skim contact sheet.'))
    }, 'image/jpeg', 0.82)
  })
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.addEventListener('load', () => resolve(reader.result), { once: true })
    reader.addEventListener('error', () => reject(reader.error), { once: true })
    reader.readAsDataURL(blob)
  })
}

async function buildContactSheets(frames, onProgress) {
  const sheetPromises = []
  for (let offset = 0; offset < frames.length; offset += FRAMES_PER_SHEET) {
    const sheetFrames = frames.slice(offset, offset + FRAMES_PER_SHEET)
    const canvas = document.createElement('canvas')
    canvas.width = CELL_WIDTH * 2
    canvas.height = CELL_HEIGHT * 2
    const context = canvas.getContext('2d', { alpha: false })
    context.fillStyle = '#101619'
    context.fillRect(0, 0, canvas.width, canvas.height)
    sheetFrames.forEach((frame, index) => {
      const column = index % 2
      const row = Math.floor(index / 2)
      context.drawImage(frame.canvas, column * CELL_WIDTH, row * CELL_HEIGHT)
      frame.canvas.width = 0
      frame.canvas.height = 0
    })
    sheetPromises.push((async () => {
      const blob = await canvasToBlob(canvas)
      const dataUrl = await blobToDataUrl(blob)
      canvas.width = 0
      canvas.height = 0
      return {
        dataUrl,
        timestamps: sheetFrames.map(frame => Math.round(frame.seconds * 100) / 100),
      }
    })())
  }
  const sheets = await Promise.all(sheetPromises)
  if (onProgress) {
    onProgress({
      phase: 'local-ready',
      progress: 78,
      message: `${sheets.length} local contact sheets ready`,
    })
  }
  return sheets
}

async function localSignature(file) {
  const chunkSize = Math.min(1024 * 1024, file.size)
  const first = new Uint8Array(await file.slice(0, chunkSize).arrayBuffer())
  const last = new Uint8Array(
    await file.slice(Math.max(0, file.size - chunkSize)).arrayBuffer(),
  )
  const metadata = new TextEncoder().encode(`${file.size}:${file.lastModified}`)
  const combined = new Uint8Array(first.length + last.length + metadata.length)
  combined.set(first, 0)
  combined.set(last, first.length)
  combined.set(metadata, first.length + last.length)
  const digest = await crypto.subtle.digest('SHA-256', combined)
  return Array.from(new Uint8Array(digest))
    .map(value => value.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, 16)
}

export async function buildCeremonySkimInput(file, { onProgress } = {}) {
  if (!file || file.size <= 0 || file.size > MAX_FILE_BYTES) {
    throw new Error('Ceremony Skim requires an MP4 no larger than 6 GiB.')
  }
  const looksLikeMp4 = file.type === 'video/mp4' || file.name.toLowerCase().endsWith('.mp4')
  if (!looksLikeMp4) throw new Error('Ceremony Skim requires an MP4 source file.')

  const started = performance.now()
  const fileUrl = URL.createObjectURL(file)
  let videos = []
  try {
    videos = await Promise.all(
      Array.from({ length: DECODER_COUNT }, () => createDecoder(fileUrl)),
    )
    const durationSeconds = videos[0].duration
    const timestamps = ceremonySkimTimestamps(durationSeconds)
    const signaturePromise = localSignature(file).catch(() => null)
    const frames = await captureFrames(videos, timestamps, onProgress)
    const sheets = await buildContactSheets(frames, onProgress)
    const previewFrames = sheets.flatMap((sheet, sheetIndex) => (
      sheet.timestamps.map((timestampSeconds, cellIndex) => ({
        dataUrl: sheet.dataUrl,
        timestampSeconds,
        cellIndex,
        frameNumber: (sheetIndex * FRAMES_PER_SHEET) + cellIndex + 1,
      }))
    ))
    return {
      source: {
        sizeBytes: file.size,
        durationSeconds,
        localSignature: await signaturePromise,
      },
      sheets,
      previewFrames,
      localOverviewMs: Math.round(performance.now() - started),
    }
  } finally {
    videos.forEach(releaseDecoder)
    URL.revokeObjectURL(fileUrl)
  }
}
